// A numeric PIN pad for the kiosk and the teacher app. Big keys, masked dots, keyboard support,
// shakes and clears on error. Not built on Base UI's OTP field: that is a row of text inputs
// driven by the OS keyboard, and the kiosk must never raise the OS keyboard.
import { useCallback, useEffect, useId, useState, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon } from "../icons.tsx";

export interface PinPadProps {
  label: ReactNode;
  /** Number of digits; default 4. */
  length?: number;
  /** Called once when the last digit is entered. */
  onComplete: (pin: string) => void;
  onChange?: (pin: string) => void;
  /** Error to show. Setting it (or bumping `errorKey`) clears the entry and shakes the dots. */
  error?: ReactNode;
  /** Change to re-trigger the shake for a repeated identical error. */
  errorKey?: string | number;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"] as const;

export function PinPad({ label, length = 4, onComplete, onChange, error, errorKey, disabled, autoFocus, className }: PinPadProps) {
  const [pin, setPin] = useState("");
  const [shaking, setShaking] = useState(false);
  const labelId = useId();
  const statusId = useId();

  useEffect(() => {
    if (!error) return;
    setPin("");
    setShaking(true);
  }, [error, errorKey]);

  const update = useCallback((next: string) => {
    if (disabled) return;
    setPin(next);
    onChange?.(next);
    if (next.length === length) onComplete(next);
  }, [disabled, length, onChange, onComplete]);

  const press = (key: (typeof KEYS)[number]) => {
    if (key === "back") update(pin.slice(0, -1));
    else if (key === "clear") update("");
    else if (pin.length < length) update(pin + key);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (/^[0-9]$/.test(e.key)) { e.preventDefault(); if (pin.length < length) update(pin + e.key); }
    else if (e.key === "Backspace") { e.preventDefault(); update(pin.slice(0, -1)); }
    else if (e.key === "Escape") { e.preventDefault(); update(""); }
  };

  return (
    <div
      className={cx("pmu-pinpad", className)} role="group" aria-labelledby={labelId} aria-describedby={statusId}
      tabIndex={-1} onKeyDown={onKeyDown} autoFocus={autoFocus} aria-disabled={disabled || undefined}
    >
      <div id={labelId} className="pmu-pinpad__label">{label}</div>
      <div
        className={cx("pmu-pinpad__dots", shaking && "pmu-pinpad__dots--shake")}
        onAnimationEnd={() => setShaking(false)} aria-hidden="true"
      >
        {Array.from({ length }, (_, i) => (
          <span key={i} className={cx("pmu-pinpad__dot", i < pin.length && "pmu-pinpad__dot--filled")} />
        ))}
      </div>
      <div id={statusId} className="pmu-sr-only" aria-live="polite">
        {`${pin.length} of ${length} digits entered`}
      </div>
      {error ? (
        <div className="pmu-pinpad__error" role="alert"><Icon name="warning" />{error}</div>
      ) : null}
      <div className="pmu-pinpad__keys">
        {KEYS.map((key) => (
          <button
            key={key} type="button" disabled={disabled}
            className={cx("pmu-pinpad__key", (key === "back" || key === "clear") && "pmu-pinpad__key--action")}
            onClick={() => press(key)}
            aria-label={key === "back" ? "Delete last digit" : key === "clear" ? "Clear PIN" : undefined}
          >
            {key === "back" ? <Icon name="backspace" /> : key === "clear" ? "Clear" : key}
          </button>
        ))}
      </div>
    </div>
  );
}
